<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'

const banners = [
  { image: 'banner_5.webp', action: 'activity_5' },
  { image: 'banner_6.webp', action: 'activity_6' },
  { image: 'banner_7.webp', action: 'activity_7' }
]

const currentIndex = ref(0)
let timer: ReturnType<typeof setInterval> | null = null

const getBannerImg = (imgName: string) => {
  return new URL(`../assets/hall/${imgName}`, import.meta.url).href
}

const startAutoPlay = () => {
  timer = setInterval(() => {
    currentIndex.value = (currentIndex.value + 1) % banners.length
  }, 3000)
}

const stopAutoPlay = () => {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
}

const handleDotClick = (index: number) => {
  currentIndex.value = index
  stopAutoPlay()
  startAutoPlay()
}

const emit = defineEmits(['bannerClick'])
const handleBannerClick = () => {
  emit('bannerClick', banners[currentIndex.value])
}

onMounted(() => {
  startAutoPlay()
})

onUnmounted(() => {
  stopAutoPlay()
})
</script>

<template>
  <section class="banner-section">
    <!-- Mobile: single full-width sliding banner -->
    <div class="mobile-banner">
      <div class="banner-slider cursor-pointer" @click="handleBannerClick">
        <transition name="fade">
          <img :key="currentIndex" :src="getBannerImg(banners[currentIndex]?.image ?? '')" class="banner-img" alt="Promo Banner" />
        </transition>
      </div>
      <div class="dots">
        <span
          v-for="(_, index) in banners"
          :key="index"
          class="dot"
          :class="{ active: index === currentIndex }"
          @click.stop="handleDotClick(index)"
        ></span>
      </div>
    </div>

    <!-- PC: multi-card horizontal strip like BetFury -->
    <div class="pc-banner">
      <div class="pc-banner-track">
        <div
          v-for="(banner, index) in banners"
          :key="index"
          class="pc-banner-card cursor-pointer"
          :class="{ active: index === currentIndex }"
          @click="() => { currentIndex = index; handleBannerClick() }"
        >
          <img :src="getBannerImg(banner.image)" class="pc-banner-img" alt="Promo Banner" />
          <!-- Active highlight border -->
          <div v-if="index === currentIndex" class="pc-card-active-border"></div>
        </div>
      </div>
      <!-- Dot indicators -->
      <div class="dots pc-dots">
        <span
          v-for="(_, index) in banners"
          :key="index"
          class="dot"
          :class="{ active: index === currentIndex }"
          @click.stop="handleDotClick(index)"
        ></span>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* ========== SHARED ========== */
.banner-section {
  width: 100%;
  padding: 10px;
  position: relative;
}

.dots {
  display: flex;
  justify-content: center;
  gap: 5px;
  margin-top: 8px;
}

.dot {
  width: 20px;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.dot.active {
  background: rgba(255, 255, 255, 0.9);
  width: 30px;
}

/* ========== MOBILE BANNER ========== */
.mobile-banner {
  display: block;
}

.banner-slider {
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.5);
  position: relative;
  aspect-ratio: 21 / 9;
  background: #111;
  display: flex;
  justify-content: center;
  align-items: center;
}

.banner-img {
  width: 100%;
  border-radius: 12px;
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  object-fit: cover;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ========== PC BANNER (hidden on mobile) ========== */
.pc-banner {
  display: none;
}

/* ========== RESPONSIVE ========== */
@media (min-width: 768px) {
  .mobile-banner {
    display: none;
  }

  .pc-banner {
    display: block;
  }

  .pc-banner-track {
    display: flex;
    gap: 10px;
    overflow-x: auto;
    scrollbar-width: none;
    padding-bottom: 4px;
  }

  .pc-banner-track::-webkit-scrollbar {
    display: none;
  }

  .pc-banner-card {
    flex: 0 0 calc(33.33% - 7px); /* 3 cards visible at once */
    min-width: 220px;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
    transition: transform 0.2s, box-shadow 0.2s;
    border: 2px solid transparent;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }

  .pc-banner-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.6);
  }

  .pc-banner-card.active {
    border-color: var(--theme-primary);
  }

  .pc-banner-img {
    width: 100%;
    height: 140px;
    object-fit: cover;
    display: block;
  }

  .pc-card-active-border {
    position: absolute;
    inset: 0;
    border-radius: 10px;
    border: 2px solid var(--theme-accent);
    pointer-events: none;
  }

  .pc-dots {
    margin-top: 10px;
  }
}

@media (min-width: 1100px) {
  .pc-banner-card {
    flex: 0 0 calc(25% - 8px); /* 4 cards visible */
  }

  .pc-banner-img {
    height: 160px;
  }
}
</style>
